/**
 * @file
 * @fileOverview This file contains the SpeechRecognizer class for speech recognition.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

class SpeechRecognizer {
  constructor(callback, onErrorCallback = null) {
    this.callback = callback;
    this.isListening = false;
    this.enable = true;
    this.onErrorCallback = onErrorCallback;
    this.permissionSet = false;
    this.checkedPermissions = false;

    // Check if the browser supports the Web Speech API
    if (
      !('webkitSpeechRecognition' in window) &&
      !('SpeechRecognition' in window)
    ) {
      alert(
        'Web Speech API is not supported by this browser. Upgrade to Chrome version 25 or later.'
      );
      return;
    }

    // Create a new instance of SpeechRecognition
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();

    // Configure the recognition instance
    this.recognition.continuous = true; // Keep listening until stopped
    this.recognition.interimResults = false; // We only want final results

    // Bind event handlers
    this.recognition.onresult = this.onResult.bind(this);
    this.recognition.onerror = this.onError.bind(this);
    this.recognition.onend = this.onEnd.bind(this);
  }

  // Start the speech recognition service
  startAndCheckPermissions() {
    if (!this.isListening && this.checkedPermissions === false) {
      this.checkPermissions()
        .then((permission) => {
          this.checkedPermissions = true;
          if (permission === 'granted') {
            this.isListening = true;
            this.recognition.start();
            this.permissionSet = true;
          } else {
            console.error('Microphone permission is not granted.');
            this.permissionSet = false;
          }
        })
        .catch((err) => {
          this.checkedPermissions = true;
        });
    }

    return this.permissionSet;
  }

  start() {
    this.isListening = true;
    this.recognition.start();
  }

  // Stop the speech recognition service
  stop() {
    if (this.isListening) {
      this.isListening = false;
      this.recognition.stop();
      console.log('Speech recognition stopped.');
    }
  }

  // Toggle the speech recognition service on and off
  toggle() {
    if (this.isListening) {
      this.stop();
    } else {
      this.start();
    }
  }

  toggleListeningAbility(toggle) {
    this.enable = toggle;
  }

  // Event handler for when a result is recognized
  onResult(event) {
    if (this.enable === true) {
      const transcript = event.results[event.resultIndex][0].transcript.trim();
      this.callback(transcript);
    }
  }

  // Event handler for when there is an error
  onError(event) {
    if (this.onErrorCallback !== null) {
      this.onErrorCallback(event);
    }
  }

  // Event handler for when the recognition ends
  onEnd() {
    if (this.isListening) {
      this.recognition.start();
    } else {
      console.log('Speech recognition service disconnected.');
    }
  }

  // Check and request microphone permissions
  async checkPermissions() {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'microphone',
      });
      if (permissionStatus.state === 'granted') {
        return 'granted';
      } else if (permissionStatus.state === 'prompt') {
        // Request permission
        await navigator.mediaDevices.getUserMedia({ audio: true });
        return 'granted';
      } else {
        return 'denied';
      }
    } catch (err) {
      throw err;
    }
  }
}

export default SpeechRecognizer;
